import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, afterNextRender, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import billingData from '../../../json/billing-management.mock.json';
import qcaBillingData from '../../../json/qca-billing.mock.json';
import qcaManagementData from '../../../json/qca-management.mock.json';
import pssManagementData from '../../../json/pss-management.mock.json';

type PssBillingRow = {
  pssCode: string;
  pssName: string;
  payableAtPss: number;
  payableToPool: number;
  receivableFromPool: number;
  interPayableToPool: number;
  interReceivableFromPool: number;
};
type QcaPssMapping = { pssCode: string; mappedOn: string };
type BillingChargeProfile = Omit<PssBillingRow, 'pssCode' | 'pssName'> & { qcaCode: string };

type BillingFixture = {
  financialYears: readonly string[];
  weekStartByFinancialYear: Record<string, string>;
  firstWeekEndByFinancialYear: Record<string, string>;
  billingRows: readonly { qcaId: number | null; qcaName: string; amount: number }[];
};

@Component({
  selector: 'app-qca-billing',
  imports: [CurrencyPipe],
  templateUrl: './qca-billing.component.html',
  styleUrl: './qca-billing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QcaBillingComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly source = billingData as BillingFixture;
  readonly qcaId = signal(Number(this.route.snapshot.paramMap.get('id')));
  readonly selectedFinancialYear = signal<string>('');
  readonly selectedWeek = signal<string>('');
  readonly appliedFinancialYear = signal<string>('');
  readonly appliedWeek = signal<string>('');
  readonly financialYears = this.source.financialYears;
  readonly weeks = computed(() => this.createWeeks(
    this.source.weekStartByFinancialYear[this.selectedFinancialYear()],
    this.source.firstWeekEndByFinancialYear[this.selectedFinancialYear()],
  ));
  readonly qca = computed(() =>
    this.source.billingRows.find((row) => row.qcaId === this.qcaId()) ?? null,
  );
  readonly pssRows = computed<readonly PssBillingRow[]>(() => {
    const profile = (qcaBillingData.qcaPssBilling as Record<string, BillingChargeProfile>)[String(this.qcaId())];
    if (!profile) return [];
    const mappings = (qcaManagementData.qcaPssMappings as Record<string, QcaPssMapping[]>)[profile.qcaCode] ?? [];
    const pssNames = new Map([
      ...qcaManagementData.pssCatalog,
      ...pssManagementData.rows,
    ].map((pss) => [pss.code, pss.name]));
    return mappings.map((mapping, index) => this.createPssBillingRow(
      mapping.pssCode,
      pssNames.get(mapping.pssCode) ?? this.fallbackPssName(mapping.pssCode),
      profile,
      index,
      mappings.length,
    ));
  });
  readonly totals = computed(() => this.pssRows().reduce((total, row) => ({
    payableAtPss: total.payableAtPss + row.payableAtPss,
    payableToPool: total.payableToPool + row.payableToPool,
    receivableFromPool: total.receivableFromPool + row.receivableFromPool,
    interPayableToPool: total.interPayableToPool + row.interPayableToPool,
    interReceivableFromPool: total.interReceivableFromPool + row.interReceivableFromPool,
  }), { payableAtPss: 0, payableToPool: 0, receivableFromPool: 0, interPayableToPool: 0, interReceivableFromPool: 0 }));
  readonly totalCharge = computed(() => {
    const total = this.totals();
    return total.payableAtPss + total.payableToPool + total.interPayableToPool - total.receivableFromPool - total.interReceivableFromPool;
  });
  readonly delayedPaymentCharge = computed(() => this.totalCharge() * 0.0125);
  readonly netPayable = computed(() => this.totalCharge() + this.delayedPaymentCharge());

  constructor() {
    afterNextRender(() => this.applyInitialWeekSelection());
  }

  changeFinancialYear(financialYear: string): void {
    this.selectedFinancialYear.set(financialYear);
    this.selectedWeek.set(this.source.weekStartByFinancialYear[financialYear] || '');
  }

  goBack(): void {
    void this.router.navigate(['/billing-management'], {
      queryParams: { financialYear: this.selectedFinancialYear(), week: this.selectedWeek() },
    });
  }

  private createWeeks(firstWeekStart: string | undefined, firstWeekEnd?: string): { id: string; label: string }[] {
    if (!firstWeekStart) return [];
    const weeks: { id: string; label: string }[] = [];
    let start = new Date(`${firstWeekStart}T00:00:00Z`);
    let end = firstWeekEnd ? new Date(`${firstWeekEnd}T00:00:00Z`) : this.addDays(start, 6);
    for (let index = 1; index <= 52; index += 1) {
      weeks.push({ id: start.toISOString().slice(0, 10), label: `Week ${index}: ${this.formatDate(start)} - ${this.formatDate(end)}` });
      start = this.addDays(end, 1);
      end = this.addDays(start, 6);
    }
    return weeks;
  }

  private getCurrentWeekSelection(): { financialYear: string; week: string } {
    const fallbackFinancialYear = this.source.financialYears[0];
    const fallbackWeek = this.source.weekStartByFinancialYear[fallbackFinancialYear] ?? '';
    const today = new Date().toISOString().slice(0, 10);
    let currentSelection: { financialYear: string; week: string } | undefined;

    for (const financialYear of this.source.financialYears) {
      const weeks = this.createWeeks(
        this.source.weekStartByFinancialYear[financialYear],
        this.source.firstWeekEndByFinancialYear[financialYear],
      );
      const currentWeek = [...weeks].reverse().find((week) => week.id <= today);

      if (currentWeek) {
        currentSelection = { financialYear, week: currentWeek.id };
      }
    }

    if (currentSelection) return currentSelection;

    const fallbackCurrentDate = `${fallbackWeek.slice(0, 4)}-${today.slice(5)}`;
    const fallbackCurrentWeek = this.createWeeks(
      fallbackWeek,
      this.source.firstWeekEndByFinancialYear[fallbackFinancialYear],
    ).reverse().find((week) => week.id <= fallbackCurrentDate);

    return {
      financialYear: fallbackFinancialYear,
      week: fallbackCurrentWeek?.id ?? fallbackWeek,
    };
  }

  private applyInitialWeekSelection(): void {
    const queryFinancialYear = this.route.snapshot.queryParamMap.get('financialYear');
    const queryWeek = this.route.snapshot.queryParamMap.get('week');
    const queryWeekIsValid = queryFinancialYear && queryWeek && this.createWeeks(
      this.source.weekStartByFinancialYear[queryFinancialYear],
      this.source.firstWeekEndByFinancialYear[queryFinancialYear],
    ).some((week) => week.id === queryWeek);
    const selection = queryWeekIsValid
      ? { financialYear: queryFinancialYear, week: queryWeek }
      : this.getCurrentWeekSelection();

    this.selectedFinancialYear.set(selection.financialYear);
    this.selectedWeek.set(selection.week);
    this.appliedFinancialYear.set(selection.financialYear);
    this.appliedWeek.set(selection.week);
  }

  private createPssBillingRow(
    pssCode: string,
    pssName: string,
    profile: BillingChargeProfile,
    index: number,
    totalRows: number,
  ): PssBillingRow {
    const amount = (value: number) => {
      const equalShare = Math.round((value / totalRows) * 100) / 100;
      return index === totalRows - 1
        ? Math.round((value - equalShare * (totalRows - 1)) * 100) / 100
        : equalShare;
    };
    return {
      pssCode,
      pssName,
      payableAtPss: amount(profile.payableAtPss),
      payableToPool: amount(profile.payableToPool),
      receivableFromPool: amount(profile.receivableFromPool),
      interPayableToPool: amount(profile.interPayableToPool),
      interReceivableFromPool: amount(profile.interReceivableFromPool),
    };
  }

  private fallbackPssName(pssCode: string): string {
    const pssNumber = pssCode.replace(/^PSS-0*/, '');
    return `Maharashtra Pooling Sub-station ${pssNumber}`;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  private formatDate(date: Date): string {
    return `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCFullYear()).slice(-2)}`;
  }
}
